import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";

import { type CurrentOfferConfigData } from "@fitness-app/data-models/entities/CurrentOfferConfig";
import { AppConfigType, type CurrentOfferAppConfig } from "@fitness-app/data-models/entities/TrainerConfig";
import { type Json } from "@fitness-app/supabase";

import { analyticsService, supabase } from "~/store/initializeStore";

// Promocje pod stałymi linkami /aktualna-oferta i /aktualna-oferta-przedluzenie z maili
// ActiveCampaign (TRE-2388, docs/aktualna-oferta.md) - app_config typu current_offer. Produkt
// ustawia właściciel na stronie konfiguracji, warianty wybiera Sales Manager na liście koszyków.

export const currentOfferQueryKey = ["app_config", AppConfigType.CURRENT_OFFER];

export const useCurrentOfferConfig = () => {
  const queryClient = useQueryClient();

  const configQuery = useQuery({
    queryKey: currentOfferQueryKey,
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("app_config")
        .select()
        .eq("type", AppConfigType.CURRENT_OFFER)
        .maybeSingle<CurrentOfferAppConfig>();

      if (error) throw error;

      return data;
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (patch: CurrentOfferConfigData) => {
      const config = configQuery.data;
      if (!config?.id) {
        throw new Error("Brak rekordu konfiguracji aktualnej oferty (app_config, typ current_offer).");
      }

      const { data, error } = await supabase
        .from("app_config")
        .update({
          data: { ...config.data, ...patch } as unknown as Json,
          updatedAt: new Date().toISOString(),
          version: (config.version ?? 0) + 1,
        })
        .eq("id", config.id)
        .eq("version", config.version ?? 0)
        .select()
        .maybeSingle<CurrentOfferAppConfig>();

      if (error) throw error;
      if (!data) {
        await queryClient.invalidateQueries({ queryKey: currentOfferQueryKey });
        // Zero rows means either a concurrent save (version moved) or RLS refusing the
        // update, and the two are indistinguishable from here.
        throw new Error(
          "Zapis nie objął żadnego wiersza: konfiguracja zmieniła się w innym miejscu albo Twoje konto nie ma prawa jej edytować.",
        );
      }

      return data;
    },
    onSuccess: (data, patch) => {
      queryClient.setQueryData(currentOfferQueryKey, data);
      if (patch.productId !== undefined) {
        analyticsService.track("current_offer_product_set", { productId: patch.productId || null });
        return;
      }
      if (patch.variantId !== undefined) {
        analyticsService.track("current_offer_variant_set", {
          audience: "new",
          productId: data.data.productId,
          variantId: patch.variantId || null,
        });
      }
      if (patch.renewalVariantId !== undefined) {
        analyticsService.track("current_offer_variant_set", {
          audience: "renewal",
          productId: data.data.productId,
          variantId: patch.renewalVariantId || null,
        });
      }
    },
    onError: (error) => {
      void message.error(error instanceof Error ? error.message : "Nie udało się zapisać aktualnej oferty.");
    },
  });

  return { configQuery, saveMutation };
};
